(() => {
  if (customElements.get('mega-menu-panel')) return;

  class MegaMenuPanel extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onWrapperEnter = this.onWrapperEnter.bind(this);
      this.onNavOpen = this.onNavOpen.bind(this);
    }

    connectedCallback() {
      this.cardsEl = this.querySelector('[data-js-mega-menu-cards]');
      this.wrapperEl = this.querySelector('.mega-menu__cards-wrapper');
      this.prevBtn = this.querySelector('[data-js-mega-menu-scroll-prev]');
      this.nextBtn = this.querySelector('[data-js-mega-menu-scroll-next]');
      this.navItem = this.closest('.navigation__item--with-collection-mega-menu');

      if (!this.cardsEl || !this.wrapperEl || !this.prevBtn || !this.nextBtn) return;

      this.setEventListeners();
      this.updateArrows();
    }

    disconnectedCallback() {
      this.removeEventListeners();

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    }

    setEventListeners() {
      this.cardsEl.addEventListener('scroll', this.onScroll, { passive: true });
      this.prevBtn.addEventListener('click', this.onPrevClick);
      this.nextBtn.addEventListener('click', this.onNextClick);
      this.wrapperEl.addEventListener('mouseenter', this.onWrapperEnter);
      this.navItem?.addEventListener('mouseenter', this.onNavOpen);

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.cardsEl);
    }

    removeEventListeners() {
      if (!this.cardsEl) return;

      this.cardsEl.removeEventListener('scroll', this.onScroll);
      this.prevBtn?.removeEventListener('click', this.onPrevClick);
      this.nextBtn?.removeEventListener('click', this.onNextClick);
      this.wrapperEl?.removeEventListener('mouseenter', this.onWrapperEnter);
      this.navItem?.removeEventListener('mouseenter', this.onNavOpen);
    }

    onScroll() {
      this.updateArrows();
    }

    onResize() {
      this.updateArrows();
    }

    onWrapperEnter() {
      this.updateArrows();
    }

    onNavOpen() {
      requestAnimationFrame(() => {
        this.updateArrows();
      });
    }

    onPrevClick(event) {
      event.preventDefault();
      this.scrollCards(-1);
    }

    onNextClick(event) {
      event.preventDefault();
      this.scrollCards(1);
    }

    getCardTargetScrollLeft(card) {
      const container = this.cardsEl;
      return (
        container.scrollLeft +
        card.getBoundingClientRect().left -
        container.getBoundingClientRect().left
      );
    }

    getActiveIndex(cards) {
      const scrollLeft = this.cardsEl.scrollLeft;
      let activeIndex = 0;

      for (let i = 0; i < cards.length; i++) {
        if (this.getCardTargetScrollLeft(cards[i]) <= scrollLeft + 1) {
          activeIndex = i;
        }
      }

      return activeIndex;
    }

    scrollCards(direction) {
      const cards = [...this.cardsEl.querySelectorAll('.mega-menu__card')];
      if (!cards.length) return;

      const activeIndex = this.getActiveIndex(cards);
      const targetIndex = activeIndex + direction;

      if (targetIndex < 0 || targetIndex >= cards.length) return;

      const targetLeft = this.getCardTargetScrollLeft(cards[targetIndex]);

      this.cardsEl.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      });

      this.snapToCard(targetLeft);
    }

    snapToCard(targetLeft) {
      const snap = () => {
        this.cardsEl.scrollLeft = targetLeft;
        this.updateArrows();
      };

      if ('onscrollend' in this.cardsEl) {
        this.cardsEl.addEventListener('scrollend', snap, { once: true });
      } else {
        window.setTimeout(snap, 350);
      }
    }

    updateArrows() {
      const maxScroll = this.cardsEl.scrollWidth - this.cardsEl.clientWidth;
      const hasOverflow = maxScroll > 1;

      if (!hasOverflow) {
        this.prevBtn.classList.add('is-disabled');
        this.nextBtn.classList.add('is-disabled');
        return;
      }

      const atStart = this.cardsEl.scrollLeft <= 1;
      const atEnd = this.cardsEl.scrollLeft >= maxScroll - 1;

      this.prevBtn.classList.toggle('is-disabled', atStart);
      this.nextBtn.classList.toggle('is-disabled', atEnd);
    }
  }

  customElements.define('mega-menu-panel', MegaMenuPanel);
})();
